import {
  toast,
  cssTransition,
  type ToastOptions,
  type Id,
} from "react-toastify";

// --- Transição customizada com bounce suave ---
export const GoonTransition = cssTransition({
  enter: "goon-toast-enter",
  exit: "goon-toast-exit",
  collapseDuration: 200,
});

// --- Spinner SVG para loading ---
const LoadingIcon = () => (
  <div
    style={{
      width: 20,
      height: 20,
      borderRadius: "50%",
      border: "2.5px solid rgba(10,173,240,0.15)",
      borderTopColor: "#153DCE",
      animation: "goon-spin 0.7s linear infinite",
      flexShrink: 0,
    }}
  />
);

// --- Ícones SVG inline (sem dependência extra de import no arquivo) ---
const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#90e200" }}
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#ef4444" }}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const WarningIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#f59e0b" }}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#153DCE" }}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// --- Defaults base ---
const BASE_OPTIONS: ToastOptions = {
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  transition: GoonTransition,
};

// --- Wrapper centralizado ---
const notify = {
  success(message: string, options?: ToastOptions): Id {
    return toast.success(message, {
      ...BASE_OPTIONS,
      icon: CheckIcon,
      ...options,
    });
  },

  error(message: string, options?: ToastOptions): Id {
    return toast.error(message, {
      ...BASE_OPTIONS,
      autoClose: 5000,
      icon: ErrorIcon,
      ...options,
    });
  },

  warning(message: string, options?: ToastOptions): Id {
    return toast.warning(message, {
      ...BASE_OPTIONS,
      icon: WarningIcon,
      ...options,
    });
  },

  info(message: string, options?: ToastOptions): Id {
    return toast.info(message, {
      ...BASE_OPTIONS,
      icon: InfoIcon,
      ...options,
    });
  },

  /** Exibe toast de loading e retorna o id para atualizar depois */
  loading(message: string, options?: ToastOptions): Id {
    return toast.loading(message, {
      ...BASE_OPTIONS,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      icon: LoadingIcon,
      ...options,
    });
  },

  /**
   * Atualiza um toast de loading existente para sucesso, erro ou warning.
   * Usa o mesmo id — sem fechar e reabrir o toast.
   */
  update(
    toastId: Id,
    type: "success" | "error" | "warning" | "info",
    message: string,
    options?: ToastOptions,
  ): void {
    const iconMap = {
      success: CheckIcon,
      error: ErrorIcon,
      warning: WarningIcon,
      info: InfoIcon,
    };

    toast.update(toastId, {
      ...BASE_OPTIONS,
      type,
      render: message,
      icon: iconMap[type],
      autoClose: type === "error" ? 5000 : 4000,
      isLoading: false,
      closeOnClick: true,
      draggable: true,
      ...options,
    });
  },

  /** Fecha um toast específico ou todos se não informar id */
  dismiss(toastId?: Id): void {
    toast.dismiss(toastId);
  },
};

export default notify;
